import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarPlus, X } from 'lucide-react';
import { TO_API } from '../TimeOff';
import { DatePickerField } from '../DatePickerField';

const todayISO = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Toronto' });

/** HR / managers enter time off for someone who phoned it in. HR can book it straight away; managers send it through approval. */
export default function BookForSomeone({ me, employee, onClose, onDone }) {
  const [people, setPeople] = useState([]);
  const [q, setQ] = useState('');
  const [emp, setEmp] = useState(employee || null);
  const [form, setForm] = useState({ date_from: todayISO(), date_to: '', type: 'vacation', note: '' });
  const [bookNow, setBookNow] = useState(!!me?.is_hr);
  const [busy, setBusy] = useState(false);

  const year = Number((form.date_from || todayISO()).slice(0, 4));
  useEffect(() => {
    axios.get(`${TO_API}/balances`, { params: { year } }).then(r => setPeople(r.data.employees || [])).catch(() => {});
  }, [year]);

  const matches = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? people.filter(p => p.employee_name.toLowerCase().includes(s)).slice(0, 8) : [];
  }, [q, people]);
  const bal = emp ? people.find(p => p.employee_id === emp.employee_id) : null;

  const submit = async () => {
    if (!emp) return toast.error('Pick who this is for');
    if (!form.date_from) return toast.error('Pick the first day');
    setBusy(true);
    try {
      const r = await axios.post(`${TO_API}/requests`, {
        ...form, date_to: form.date_to || form.date_from, employee_id: emp.employee_id, approve_now: bookNow && !!me?.is_hr,
      });
      const booked = r.data.request?.status === 'approved';
      toast.success(booked ? `${emp.employee_name}'s time off is booked — they've been emailed` : `Request entered for ${emp.employee_name} — it's in the approval queue`);
      if (r.data.warning) toast.warning(r.data.warning, { duration: 9000 });
      onDone?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not enter it'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" data-testid="to-book-modal" onClick={onClose}>
      <div className="bg-white rounded-xl p-5 w-full max-w-md space-y-3" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2"><CalendarPlus className="w-4 h-4 text-emerald-600" /> Enter time off for someone</h3>
          <button onClick={onClose} className="p-1 rounded text-slate-500 hover:bg-slate-100" data-testid="to-book-close"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-xs text-slate-600">For when a person phones in a day off — you put it in for them{me?.is_hr ? ' and can book it on the spot' : ' and it goes through the normal approval'}.</p>

        <div className="text-xs text-slate-600">Who
          {emp ? (
            <div className="mt-1 flex items-center justify-between gap-2 border border-slate-300 rounded-lg px-2 py-2 text-sm bg-slate-50" data-testid="to-book-emp">
              <span className="font-medium text-slate-900">{emp.employee_name}</span>
              <span className="text-xs text-slate-500">{bal ? `${bal.remaining - bal.pending} vacation day${bal.remaining - bal.pending === 1 ? '' : 's'} left in ${year}` : ''}</span>
              {!employee && <button onClick={() => { setEmp(null); setQ(''); }} className="text-xs text-blue-700 font-semibold" data-testid="to-book-change-emp">change</button>}
            </div>
          ) : (
            <div className="relative mt-1">
              <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Type a name…" className="w-full border border-slate-300 rounded-lg px-2 py-2 text-sm" data-testid="to-book-search" />
              {matches.length > 0 && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-56 overflow-auto">
                  {matches.map(p => (
                    <button key={p.employee_id} onClick={() => { setEmp(p); setQ(''); }} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex justify-between gap-2" data-testid={`to-book-opt-${p.employee_id}`}>
                      <span className="text-slate-900">{p.employee_name}</span>
                      <span className="text-xs text-slate-500">{p.remaining - p.pending} left</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="text-xs text-slate-600">First day<DatePickerField value={form.date_from} testid="to-book-from" placeholder="Tap to pick"
            onChange={v => setForm(f => ({ ...f, date_from: v, date_to: !f.date_to || (v && f.date_to < v) ? v : f.date_to }))}
            className="mt-1 w-full border border-slate-300 rounded-lg px-2 py-2 text-sm bg-white" /></div>
          <div className="text-xs text-slate-600">Last day<DatePickerField value={form.date_to} min={form.date_from} testid="to-book-to" placeholder="Same day"
            onChange={v => setForm(f => ({ ...f, date_to: v }))}
            className="mt-1 w-full border border-slate-300 rounded-lg px-2 py-2 text-sm bg-white" /></div>
        </div>
        <label className="text-xs text-slate-600 block">Type
          <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} className="mt-1 w-full border border-slate-300 rounded-lg px-2 py-2 text-sm" data-testid="to-book-type">
            <option value="vacation">Vacation (counts against entitlement)</option>
            <option value="personal">Personal day</option>
            <option value="unpaid">Unpaid leave</option>
          </select>
        </label>
        <label className="text-xs text-slate-600 block">Note (optional)
          <input value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} placeholder="e.g. called in — appointment" className="mt-1 w-full border border-slate-300 rounded-lg px-2 py-2 text-sm" data-testid="to-book-note" />
        </label>
        {me?.is_hr && (
          <label className="flex items-start gap-2 text-sm text-slate-800 cursor-pointer" data-testid="to-book-now-label">
            <input type="checkbox" checked={bookNow} onChange={e => setBookNow(e.target.checked)} className="mt-1" data-testid="to-book-now" />
            <span><b>Book it now</b> — skip approval, it goes straight on the calendar and schedule and {emp ? emp.employee_name.split(' ')[0] : 'they'} get{emp ? 's' : ''} an email.<br /><span className="text-xs text-slate-500">Untick to send it through HR review → manager approval instead.</span></span>
          </label>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="px-3 py-2 text-sm text-slate-600">Cancel</button>
          <button onClick={submit} disabled={busy || !emp} className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50" data-testid="to-book-submit">
            {busy ? 'Saving…' : bookNow && me?.is_hr ? 'Book time off' : 'Send for approval'}
          </button>
        </div>
      </div>
    </div>
  );
}
